/**
 * @module app.core
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/api/api';
import { ProviderService } from 'app/cd/services/provider-service';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { MemberService } from 'app/cd/services/member-service';
import { IPopup } from 'app/shared/elements/popup-base';
import { deepClone } from 'app/shared/lib/utilities';
import * as CD from 'app/cd/models/essette-api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';

@autoinject
export class memberProvidersDetail extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView })
    public primaryIdentifier: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public memberPlanId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public editedProvider: CD.MemberProvider | null = null;

    @bindable
    public memberProviderModal: IPopup | null = null;

    @bindable
    protected includeInactive: boolean = false;

    protected memberProviders: Array<CD.MemberProvider> = [];
    protected showProviderSummaryLink: boolean = false;
    protected openProviderSummary = openProviderSummary;
    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 10;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly providerService: ProviderService,
        private readonly memberService: MemberService,
        private readonly element: Element,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.memberPlanId) { this.logger.warn('Bindable property [member-plan-id] was not set at binding'); }

        this.checkPermissions();
        this.reload();
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadMemberProviders();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected openMemberProviderModal(provider: CD.MemberProvider | null): void {
        this.editedProvider = provider ? deepClone(provider) : null;
        if(!!this.memberProviderModal) this.memberProviderModal.open();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberProviders(): Promise<void> {
        if(!this.memberPlanId) return;

        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        const includes: string[] = [
            'member',
            'member.plan',
            'member.plan.healthPlan',
            'member.plan.lineOfBusiness',
            'provider',
            'provider.location',
            'provider.location.facility',
            'provider.location.facility.address.physical',
            'provider.location.provider',
            'relationship'
        ];

        const fields: string[] = [
            'member.plan.healthPlan.$display',
            'member.plan.lineOfBusiness.$display',
            'provider.$display',
            'provider.location.$display',
            'provider.location.provider.identifier.nationalProvider',
            'provider.location.provider.identifier.primary',
            'provider.location.facility.identifier.nationalProvider',
            'provider.location.facility.address.physical.street1',
            'provider.location.facility.address.physical.street2',
            'provider.location.facility.address.physical.city',
            'provider.location.facility.address.physical.region.code',
            'provider.location.facility.address.physical.postalCode',
            'provider.location.facility.address.physical.$display',
            'relationship.$display',
            'dateRange.start',
            'dateRange.end'
        ]

        try {
            this.state.beginLoad();

            const response = await this.providerService.loadMemberProvidersByPlan(this.memberPlanId, options, includes, undefined, fields);
            this.memberProviders = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateMemberProvider(memberProvider: CD.MemberProvider): Promise<void> {
        if(!memberProvider.id) return;

        // Show confirmation dialog only if deactivating
        if(memberProvider.isActive) {
            const result = await confirmation.showDeactivate('cd:memberProviderRelationship.$', 1, false);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.memberService.activateMemberProvider(memberProvider.id, !memberProvider.isActive);
            isSuccessful = response.isSuccessful;
            this.reload();
            // need to trigger a reload of the member info so PCP display is updated
            this.element.dispatchEvent(new CustomEvent('reload-member', { bubbles: true }));
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('cd:memberProviderRelationship.$', !memberProvider.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
